/**
 * frontend/src/state/userLimits.jsx — Estado oficial del Primer Mes Gratis y
 * del Plan Gratis del usuario actual.
 *
 * Reglas implementadas (lado frontend, fuente de verdad = backend
 * /api/free-tier/status):
 *
 *  - Primer Mes Gratis: 30 mensajes totales en 30 dias desde created_at.
 *    Durante el trial NO aplica el limite de 6/dia.
 *  - Plan Gratis (post-trial): 6 mensajes diarios.
 *  - Estado de proyectos: cuota mensual del workspace (2 proyectos,
 *    1 cambio de tipo).
 *
 * Expone:
 *  - <UserLimitsProvider> que envuelve la app y refresca el estado.
 *  - useUserLimits() hook con todo el estado + helpers.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const UserLimitsContext = createContext(null);

const DEFAULT_STATE = {
  // free_tier (mensajes)
  is_premium: false,
  is_trial_active: false,
  trial_started_at: null,
  trial_ends_at: null,
  trial_messages_used: 0,
  trial_messages_remaining: 0,
  trial_messages_max: 100,
  daily_messages_used: 0,
  daily_messages_remaining: 0,
  daily_messages_max: 8,
  can_send_message: true,
  // 🟥 P26.35 · `mode` arranca en null = DESCONOCIDO. Antes arrancaba en
  // 'daily', así que ante cualquier fallo de red / 401 transitorio la UI
  // afirmaba "Plan Gratis — 0 mensajes" incluso a cuentas Premium/creador.
  mode: null,  // null (desconocido) | premium | trial | daily | blocked
  // Solo `true` cuando /api/free-tier/status respondió OK. Toda la UI de
  // planes (banner de cuota, chip del header) DEBE mirar este flag.
  plan_known: false,

  // workspace_quota (proyectos)
  works_created: 0,
  works_remaining: 0,
  works_max: 2,
  type_changes_used: 0,
  type_changes_remaining: 1,
  can_create: true,
  can_change_type: true,

  // meta
  loading: true,
  loaded_at: null,
};


export const UserLimitsProvider = ({ children }) => {
  const [state, setState] = useState(DEFAULT_STATE);
  // Token actual (puede cambiar tras login/logout). Se observa via storage
  // event y un evento custom emitido por App.js al loguear/desloguear.
  const [authVersion, setAuthVersion] = useState(0);
  const retryRef = React.useRef(0);
  const refreshRef = React.useRef(null);

  const refresh = useCallback(async () => {
    // P2.1 cookie-only: la cookie httpOnly se envia automaticamente
    // (axios.defaults.withCredentials=true en index.js). Nada de localStorage,
    // nada de Authorization header manual.
    try {
      // Cargar ambos estados en paralelo. Si responden 401 (sin sesion)
      // caemos al catch y reseteamos al DEFAULT_STATE — aislamiento estricto.
      const [ftRes, wsRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/free-tier/status`),
        axios.get(`${API_URL}/api/damyxia/modules/quota/status`),
      ]);

      // Si AMBAS llamadas fallaron con 401 (no hay sesion), reset a default.
      const bothFailed = ftRes.status !== 'fulfilled' && wsRes.status !== 'fulfilled';
      const ftIsAuth401 = ftRes.status !== 'fulfilled'
        && ftRes.reason?.response?.status === 401;
      if (bothFailed || ftIsAuth401) {
        setState({ ...DEFAULT_STATE, loading: false, loaded_at: new Date().toISOString() });
        // Reintento único si NO fue un 401 (red caída, 5xx, backend ocupado):
        // sin esto el estado queda en "desconocido" hasta el próximo focus.
        if (!ftIsAuth401 && retryRef.current < 2) {
          retryRef.current += 1;
          setTimeout(() => { refreshRef.current?.(); }, 4000);
        }
        return;
      }

      const ftOk = ftRes.status === 'fulfilled';
      if (!ftOk && retryRef.current < 2) {
        retryRef.current += 1;
        setTimeout(() => { refreshRef.current?.(); }, 4000);
      }
      if (ftOk) retryRef.current = 0;

      const ft = ftRes.status === 'fulfilled' ? ftRes.value.data : {};
      const ws = wsRes.status === 'fulfilled' ? wsRes.value.data : {};

      setState((prev) => ({
        ...prev,
        is_premium: !!ft.is_premium,
        is_trial_active: !!ft.is_trial_active,
        trial_started_at: ft.trial_started_at ?? null,
        trial_ends_at: ft.trial_ends_at ?? null,
        trial_messages_used: ft.trial_messages_used ?? 0,
        trial_messages_remaining: ft.trial_messages_remaining ?? 0,
        trial_messages_max: ft.trial_messages_max ?? 100,
        daily_messages_used: ft.daily_messages_used ?? 0,
        daily_messages_remaining: ft.daily_messages_remaining ?? 0,
        daily_messages_max: ft.daily_messages_max ?? 8,
        can_send_message: ft.can_send_message ?? true,
        mode: ftOk ? (ft.mode ?? 'daily') : null,
        plan_known: ftOk,
        works_created: ws.works_created ?? 0,
        works_remaining: ws.works_remaining ?? 0,
        works_max: ws.works_max ?? 2,
        type_changes_used: ws.type_changes_used ?? 0,
        type_changes_remaining: ws.type_changes_remaining ?? 0,
        can_create: ws.can_create ?? true,
        can_change_type: ws.can_change_type ?? true,
        loading: false,
        loaded_at: new Date().toISOString(),
      }));
    } catch {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    refreshRef.current = refresh;
    retryRef.current = 0;
    refresh();
    // Refresh perezoso al volver al tab (evita estado obsoleto tras enviar
    // mensajes desde otra pestana).
    const onFocus = () => refresh();
    // Cambio de auth en otra pestana o explicito desde App.js.
    const onAuthChange = () => {
      setState(DEFAULT_STATE);  // reset INMEDIATO (aislamiento estricto).
      setAuthVersion((v) => v + 1);
    };
    const onStorage = (e) => {
      if (e.key === 'damyxia_token' || e.key === 'access_token' || e.key === null) {
        onAuthChange();
      }
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('damyxia:auth-changed', onAuthChange);
    window.addEventListener('storage', onStorage);
    // Botón "Refrescar Dashboard" del header → forzar re-fetch inmediato.
    const onRefreshDashboard = () => { refresh(); };
    window.addEventListener('damyxia:refresh-dashboard', onRefreshDashboard);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('damyxia:auth-changed', onAuthChange);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('damyxia:refresh-dashboard', onRefreshDashboard);
    };
  }, [refresh, authVersion]);

  // Helpers derivados.
  const isProjectThirdBlocked = !state.is_premium && !state.can_create;
  // 🟥 P26.35 — nunca declarar bloqueo si el plan es DESCONOCIDO.
  const isMessageBlocked = state.plan_known && !state.is_premium && !state.can_send_message;

  const value = {
    ...state,
    refresh,
    isProjectThirdBlocked,
    isMessageBlocked,
  };

  return (
    <UserLimitsContext.Provider value={value}>
      {children}
    </UserLimitsContext.Provider>
  );
};


export const useUserLimits = () => {
  const ctx = useContext(UserLimitsContext);
  if (!ctx) {
    // Permitir uso fuera del provider con defaults (no romper componentes
    // legacy si alguno los importa antes de que el provider monte).
    return { ...DEFAULT_STATE, refresh: async () => {}, isProjectThirdBlocked: false, isMessageBlocked: false };
  }
  return ctx;
};

export default UserLimitsContext;
